import React, { Fragment } from "react";
import styled from "styled-components";
import * as R from "ramda";
import moment from "moment-timezone";
import {
  Form,
  Input,
  Select,
  Spin,
  Checkbox,
  Upload,
  TimePicker,
  Row,
  Col,
  Tag,
  Icon,
  Tooltip,
  Switch
} from "antd";
import { connect, useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import {
  toClass,
  compose,
  lifecycle,
  withProps,
  withHandlers
} from "recompose";
import { FormItem } from "@worcket/common/components/controls";
import { dispatchOnMount, isLoading } from "@worcket/common/hocs";

import Element from "../../../../../../components/structure/Element";
import processStepTypes from "../../../../../../constants/processStepTypes";
import getChatbotMessage from "../../../../../../utils/getChatbotMessage";
import {
  fetchOpportunityRooms,
  FETCH_OPPORTUNITY_ROOMS,
  uploadOpportunityDocument,
  updateOpportunityStep,
  UPDATE_OPPORTUNITY_STEP
} from "../../../../../../store/actions/opportunity";
import { FETCH_TESTS } from "../../../../../../store/actions/catalogs";
import {
  selectors as chatbotSelectors,
  validators as chatbotValidators
} from "../../../../../../components/Chatbot";
import OpportunityChatbot from "../../../../routes/OpportunityChatbot";
import { DropdownContainer } from "../../../../../../components/inputs/SelectWithIcons";
import StepTypeOption from "../../../../../../components/inputs/StepTypeOption";
import TestOption from "../../../../../../components/inputs/TestOption";
import Button from "../../../../../../components/Button";

import { transformToApi, transformFromApi } from "./selectors";
import Rooms from "./Rooms";

const { Option, OptGroup } = Select;
const TextArea = Input.TextArea;
const defaultStepType = R.head(R.keys(processStepTypes));
const getProcessStepTypeConfig = stepType =>
  processStepTypes[stepType] || processStepTypes[defaultStepType] || {};

const DOCUMENT_UPLOAD_TYPES = [
  ["identity_document", "idcard"],
  ["cv", "profile"],
  ["certificate", "safety-certificate"],
  ["degree", "read"],
  ["proof", "file-done"],
  ["image", "picture"],
  ["form", "file-text"],
  ["work_document", "solution"],
  ["other", "paper-clip"]
];

// "single" is the default upload mode, so its suffix only adds noise to the
// document name that is later substituted into the chatbot message tags.
const getDocumentUploadName = (t, documentType, uploadMode) => {
  const safeDocumentType = documentType || "identity_document";
  const documentTypeLabel = t(
    `opportunity:process.stepTypes.document_upload.documentTypes.${safeDocumentType}`
  );

  if (!uploadMode || uploadMode === "single") {
    return documentTypeLabel;
  }

  return `${documentTypeLabel} - ${t(
    `opportunity:process.stepTypes.document_upload.uploadModes.${uploadMode}`
  )}`;
};

const getDocumentUploadOptions = t =>
  R.chain(([documentType, icon]) => {
    const uploadModes =
      documentType === "identity_document"
        ? ["single", "front_back"]
        : ["single"];

    return uploadModes.map(uploadMode => ({
      documentType,
      uploadMode,
      icon,
      label: getDocumentUploadName(t, documentType, uploadMode)
    }));
  }, DOCUMENT_UPLOAD_TYPES);

const normalizeDocumentOptionLabel = label =>
  String(label || "").replace(/\s+[–—-]\s+/g, " - ");

const SpinContainer = styled.div`
  width: 100%;
  margin: 60px 0;
  text-align: center;
`;

const TagsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  background-color: #fff;

  .title {
    display: inline-block !important;
    margin-right: 4px;
    margin-bottom: 0;
  }
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);

  > * + * {
    margin-left: 12px;
  }
`;

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
`;

const SwitchInfo = styled.p`
  margin-left: 10px;
  margin-bottom: 0;
  line-height: normal;
`;

const TemplateRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
`;

const TemplateUploadButton = styled(Button)`
  border: 1px dashed #155bff !important;
  border-radius: 8px !important;
  color: #155bff !important;
  font-weight: 800 !important;
  min-width: 260px;
`;

const TemplateHelp = styled.span`
  color: #7d8aa3;
  font-size: 13px;
`;

const tagList = [
  { tag: "[Nombre del Candidato]", condition: R.T },
  {
    tag: "[Nombre del Reclutador]",
    condition: stepType => stepType.endsWith("interview")
  },
  {
    tag: "[Dirección]",
    condition: stepType => stepType === "presential-interview"
  },
  { tag: "[URL]", condition: stepType => stepType === "remote-interview" },
  {
    tag: "[Teléfono]",
    condition: stepType => stepType === "tel-interview",
    checkInputNotEmpty: true
  },
  { tag: "[Nombre del Test]", condition: stepType => stepType === "test" },
  {
    tag: "[Nombre del Documento]",
    condition: stepType => stepType === "document_upload"
  },
  {
    tag: "[Nombre de la Plantilla]",
    condition: stepType => stepType === "document_upload"
  }
];

const getAvailableTags = ({ stepType, stepName, form }) => {
  const tagStepType = stepName === "selected" ? "chat-message" : stepType;

  return R.filter(
    tag =>
      tag.condition(tagStepType, {
        documentType: form.getFieldValue("config.documentType")
      }),
    tagList
  );
};

const isSentinelTest = test =>
  test.source === "worcket" && String(test.idTest) === "8";

const PROCESS_DOCUMENT_TYPES = [
  "identity_document",
  "cv",
  "certificate",
  "degree",
  "proof",
  "image",
  "form",
  "work_document",
  "other"
];

const getUploadModesForDocumentType = documentType =>
  documentType === "identity_document" ? ["single", "front_back"] : ["single"];

const getDocumentRequirementOptions = t =>
  R.chain(
    documentType =>
      getUploadModesForDocumentType(documentType).map(uploadMode => ({
        documentType,
        uploadMode,
        label: getDocumentUploadName(t, documentType, uploadMode),
        value: `${documentType}::${uploadMode}`
      })),
    PROCESS_DOCUMENT_TYPES
  );

const getDocumentRequirementKey = config =>
  config && config.documentType && config.uploadMode
    ? `${config.documentType}::${config.uploadMode}`
    : undefined;

const getCustomDocumentTypeName = (config, t) => {
  if (!config || config.documentType !== "other") return undefined;

  const defaultOtherName = getDocumentUploadName(t, "other", config.uploadMode);
  const customDocumentTypeName = R.prop("customDocumentTypeName", config);
  const documentName = R.prop("documentName", config);

  return (
    customDocumentTypeName ||
    (documentName && documentName !== defaultOtherName
      ? documentName
      : undefined)
  );
};

const updateDocumentRequirementName = (config, t) => {
  if (!config || !config.documentType) return config;

  const customDocumentTypeName = getCustomDocumentTypeName(config, t);

  if (config.documentType === "other") {
    return R.pipe(R.assoc("customDocumentTypeName", customDocumentTypeName))(
      config
    );
  }

  return R.pipe(
    R.dissoc("customDocumentTypeName"),
    R.assoc(
      "documentName",
      getDocumentUploadName(t, config.documentType, config.uploadMode)
    )
  )(config);
};

const applyDocumentRequirementConfig = (values, documentRequirementOptions) => {
  const selected = documentRequirementOptions.find(
    option =>
      option.value === R.path(["config", "documentRequirementKey"], values) ||
      (option.documentType === R.path(["config", "documentType"], values) &&
        option.uploadMode === R.path(["config", "uploadMode"], values)) ||
      option.label === R.path(["config", "documentName"], values)
  );

  if (!selected) return values;

  const customDocumentTypeName = R.path(
    ["config", "customDocumentTypeName"],
    values
  );
  const documentName =
    selected.documentType === "other" && customDocumentTypeName
      ? String(customDocumentTypeName).trim()
      : selected.label;

  const config = R.pipe(
    R.assocPath(["config", "documentType"], selected.documentType),
    R.assocPath(["config", "uploadMode"], selected.uploadMode),
    R.assocPath(["config", "documentName"], documentName)
  )(values);

  return R.pipe(
    R.dissocPath(["config", "idQuestion"]),
    R.dissocPath(["config", "documentRequirementId"]),
    R.dissocPath(["config", "customDocumentTypeName"]),
    R.dissocPath(["config", "documentRequirementKey"])
  )(config);
};

const Tags = ({
  tags = [],
  textInputRef,
  form,
  inputKey,
  t,
  insertTagInMessage,
  fieldName
}) => (
  <TagsWrapper>
    <h5 className="title">
      {t("opportunity:process.stepDetails.availableTags")}
    </h5>
    {R.pipe(
      R.filter(R.prop("tag")),
      R.map(({ tag, checkInputNotEmpty }) => {
        const isDisabled =
          checkInputNotEmpty && !form.getFieldValue(`config.${inputKey}`);
        const Wrapper = isDisabled ? Tooltip : Fragment;
        const wrapperProps = isDisabled
          ? {
              title: t("opportunity:process.stepDetails.telephoneRequired")
            }
          : {};
        return (
          <Wrapper key={tag} {...wrapperProps}>
            <Tag
              style={isDisabled && { opacity: 0.4, cursor: "not-allowed" }}
              onClick={
                !isDisabled &&
                (() => insertTagInMessage(textInputRef, tag, fieldName))
              }
            >
              {tag}
            </Tag>
          </Wrapper>
        );
      })
    )(tags)}
    <Tooltip
      title={t("opportunity:process.stepDetails.availableTagsDescription")}
    >
      <Icon type="question-circle" />
    </Tooltip>
  </TagsWrapper>
);

const TypePicker = toClass(
  ({ t, handleTypeChange, savingStep, onChange, ...props }) => (
    <Select
      onChange={value => {
        onChange(value);
        setTimeout(() => {
          handleTypeChange(value);
        }, 0);
      }}
      data-test={props.value}
      id="type"
      dropdownRender={menu => <DropdownContainer>{menu}</DropdownContainer>}
      {...props}
      disabled={savingStep}
    >
      {R.keys(processStepTypes).map(interviewType => (
        <Option
          key={interviewType}
          value={interviewType}
          data-test={interviewType}
        >
          <StepTypeOption t={t} typeKey={interviewType} />
        </Option>
      ))}
    </Select>
  )
);

const SchedulePicker = toClass(({ value, onChange, disabled, t }) => (
  <div>
    <Form.Item
      style={{ display: "inline-block", marginRight: 15, marginBottom: 10 }}
      label={t("opportunity:process.stepDetails.from")}
    >
      <TimePicker
        value={value.from}
        onChange={time => onChange(R.assoc("from", time, value))}
        format="HH:mm"
        minuteStep={10}
        placeholder={t("opportunity:process.stepDetails.from")}
        className="from-time-picker-test"
        disabled={disabled}
        id="from"
      />
    </Form.Item>
    <Form.Item
      style={{ display: "inline-block", marginBottom: 10 }}
      label={t("opportunity:process.stepDetails.to")}
    >
      <TimePicker
        value={value.to}
        onChange={time => onChange(R.assoc("to", time, value))}
        format="HH:mm"
        minuteStep={10}
        placeholder={t(`opportunity:process.stepDetails.to`)}
        disabled={disabled}
        id="to"
      />
    </Form.Item>
  </div>
));

const Wrapper = styled.div`
  background-color: rgba(0, 0, 0, 0.02);
  box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.05);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  /* Change padding to align with step title in ProcessStep.js */
  padding: 24px 66px 32px 66px;

  .ant-form-item {
    margin-bottom: 20px;
  }

  .rooms-container .ant-form-explain {
    margin-top: 20px;
  }
`;

const MessageOnlyLayout = styled.div`
  max-width: 720px;
`;

const MessageOnlyField = styled.div`
  margin-bottom: 20px;

  label {
    display: block;
    margin-bottom: 8px;
    color: rgba(0, 0, 0, 0.65);
    font-size: 14px;
    line-height: 1.5;
  }
`;

const StepConfiguration = ({
  t,
  form,
  step,
  companyUsers,
  documentRequirementOptions,
  onSubmit,
  uploadOpportunityDocument,
  stepLoading,
  savingStep,
  stepType,
  stepTypeConfig,
  inputKey,
  insertTagInMessage,
  weekDaysOptions,
  handleTypeChange,
  scheduleValidator,
  roomsValidator,
  autoScheduleValidator,
  candidatesList,
  tests,
  onSave,
  isWizard,
  chatbotMessageOnly
}) => {
  const messageInputRefs = [React.useRef(), React.useRef()];
  const [uploadingTemplate, setUploadingTemplate] = React.useState(false);
  const canEditDisplayName = ["selected", "accepted"].includes(step.name);

  const hasSuccessProbabilityTest = useSelector(state => {
    let res = R.pathOr(
      undefined,
      ["company", "services", "successProbabilityTest"],
      state
    );
    if (res === undefined) {
      res = R.pathOr(
        false,
        ["headquarter", "services", "successProbabilityTest"],
        state
      );
    }
    return res;
  });

  if (!hasSuccessProbabilityTest) {
    tests = R.reject(t => t.key === "successProbability-worcket", tests);
  }

  const documentUploadOptions = React.useMemo(
    () => getDocumentUploadOptions(t),
    [t]
  );
  const selectedDocumentUploadOption = R.find(
    option =>
      normalizeDocumentOptionLabel(option.label) ===
      normalizeDocumentOptionLabel(form.getFieldValue("config.documentName")),
    documentUploadOptions
  );
  const selectedDocumentType =
    form.getFieldValue("config.documentType") ||
    R.prop("documentType", selectedDocumentUploadOption);
  const isFormDocumentUpload = selectedDocumentType === "form";
  const isOtherDocumentUpload = selectedDocumentType === "other";

  const uploadDocumentTemplate = React.useCallback(
    async file => {
      setUploadingTemplate(true);

      try {
        const uploaded = await uploadOpportunityDocument({
          id: step.idOpportunity,
          file
        });

        if (uploaded && uploaded.id) {
          form.setFieldsValue({
            "config.templateFile": uploaded.location,
            "config.templateFileName": uploaded.fileName
          });
        }
      } finally {
        setUploadingTemplate(false);
      }

      return false;
    },
    [form, step.idOpportunity, uploadOpportunityDocument]
  );

  if (chatbotMessageOnly) {
    return (
      <Wrapper>
        <MessageOnlyLayout>
          {canEditDisplayName && (
            <FormItem
              form={form}
              label={t("opportunity:process.stepDetails.name")}
              name="displayName"
              render={
                <Input
                  data-test="step-display-name-input"
                  disabled={savingStep}
                />
              }
            />
          )}
          <MessageOnlyField>
            <label>{t("opportunity:process.processEditor.stepType")}</label>
            <Input
              value={t("opportunity:process.stepDetails.chatbotMessage")}
              disabled
            />
          </MessageOnlyField>
          <FormItem
            form={form}
            label={t("opportunity:process.stepDetails.chatbotMessage")}
            name="config.message"
            rules={[
              {
                required: true,
                whitespace: true,
                message: t("form:emptyFieldValidation")
              }
            ]}
            render={
              <TextArea
                ref={messageInputRefs[0]}
                disabled={savingStep}
                autoSize={{ minRows: 5, maxRows: 10 }}
              />
            }
          />
          <Tags
            tags={getAvailableTags({
              stepType: "chat-message",
              stepName: step.name,
              form
            })}
            textInputRef={messageInputRefs[0]}
            form={form}
            inputKey={inputKey}
            t={t}
            insertTagInMessage={insertTagInMessage}
          />
          <Buttons>
            <Button
              type="primary"
              loading={savingStep}
              onClick={e => {
                e.preventDefault();
                onSubmit();
              }}
              data-test="save-step-details-chatbot"
            >
              {t("opportunity:process.stepDetails.saveStep")}
            </Button>
          </Buttons>
        </MessageOnlyLayout>
      </Wrapper>
    );
  }

  // const renderTests = tests => {
  //   const [premiumTests, freeTests] = R.partition(
  //     R.propEq("paid", true),
  //     tests
  //   );

  //   return (
  //     <>
  //       {renderTestOptions(
  //         freeTests,
  //         t("opportunity:process.stepTypes.test.freeTests"),
  //         premiumTests.length > 0
  //       )}
  //       {renderTestOptions(
  //         premiumTests,
  //         t("opportunity:process.stepTypes.test.premiumTests"),
  //         freeTests.length > 0
  //       )}
  //     </>
  //   );
  // };

  const renderTests = tests => {
    const selectableTests = tests.filter(
      test =>
        test.selectableInQuestionsCatalog !== false && !isSentinelTest(test)
    );
    const [validationTests, evaluationTests] = R.partition(
      R.propEq("validation", true),
      selectableTests
    );

    return [
      renderTestOptions(
        validationTests,
        t("opportunity:process.stepTypes.test.validationTests"),
        validationTests.length > 0
      ),
      renderTestOptions(
        evaluationTests,
        t("opportunity:process.stepTypes.test.evaluationTests"),
        evaluationTests.length > 0
      )
    ];
  };

  const renderTestOptions = (tests, label, showLabel) => {
    const sortedTests = R.pipe(
      R.groupBy(R.prop("source")),
      R.values,
      R.map(R.sortWith([R.ascend(R.prop("idTest"))])),
      R.flatten
    )(tests);
    const options = sortedTests.map(({ key, source, idTest, name, cost }) => (
      <Option key={`${source}-${idTest}`} value={`${source}-${idTest}`}>
        <TestOption
          t={t}
          testKey={key}
          source={source}
          idTest={idTest}
          name={name}
          cost={cost > 0 && cost}
        />
      </Option>
    ));
    return showLabel ? (
      <OptGroup key={label} label={label}>
        {options}
      </OptGroup>
    ) : (
      options
    );
  };

  return (
    <Wrapper isWizard={isWizard}>
      {stepLoading ? (
        <SpinContainer>
          <Spin />
        </SpinContainer>
      ) : (
        <Form onSubmit={onSubmit}>
          <Row gutter={24}>
            <Col span={12}>
              <FormItem
                style={{ display: step.default ? "none" : "block" }}
                label={t("opportunity:process.stepDetails.name")}
                form={form}
                name="name"
                rules={
                  !step.default
                    ? [
                        {
                          required: true,
                          message: t("form:emptyFieldValidation")
                        }
                      ]
                    : []
                }
                render={
                  <Input data-test="step-name-input" disabled={savingStep} />
                }
              />
              {canEditDisplayName && (
                <FormItem
                  label={t("opportunity:process.stepDetails.name")}
                  form={form}
                  name="displayName"
                  render={
                    <Input
                      data-test="step-display-name-input"
                      disabled={savingStep}
                    />
                  }
                />
              )}
            </Col>
            <Col span={12}>
              {step.name !== "selected" && (
                <>
                  <FormItem
                    label={t("opportunity:process.stepDetails.type")}
                    form={form}
                    name="type"
                    initialValue={R.head(R.keys(processStepTypes))}
                    rules={[
                      {
                        required: true,
                        message: t("form:emptyFieldValidation")
                      }
                    ]}
                    render={
                      <TypePicker
                        t={t}
                        handleTypeChange={handleTypeChange}
                        savingStep={savingStep}
                      />
                    }
                  />
                </>
              )}
            </Col>
          </Row>
          {step.name !== "selected" && stepTypeConfig.interviewer !== false && (
            <FormItem
              label={t("opportunity:process.stepDetails.recruiter")}
              form={form}
              name="config.users"
              rules={[
                ...(form.getFieldValue("type") !== "chat-message"
                  ? [
                      {
                        required: true,
                        message: t("form:emptyFieldValidation")
                      }
                    ]
                  : []),
                {
                  validator: (rule, value, callback) => {
                    form.validateFields(["config.autoSchedule"], {
                      force: true
                    });
                    callback();
                  }
                }
              ]}
              render={
                <Select
                  allowClear={form.getFieldValue("type") === "chat-message"}
                  showSearch={true}
                  optionFilterProp="children"
                  labelInValue={true}
                  disabled={savingStep}
                  className="interviewers-select-test"
                >
                  {companyUsers.map(user => (
                    <Option value={user.id} key={user.id}>
                      {user.name}
                    </Option>
                  ))}
                </Select>
              }
            />
          )}
          {step.name !== "selected" && (
            <>
              {stepType === "test" && (
                <FormItem
                  form={form}
                  label={t("opportunity:process.stepTypes.test.idTest")}
                  name={"config.testId"}
                  data-test="interviews-custom-input"
                  rules={[
                    {
                      required: true,
                      message: t("form:emptyFieldValidation")
                    }
                  ]}
                  render={
                    <Select
                      disabled={savingStep}
                      className="test-select"
                      dropdownRender={menu => (
                        <DropdownContainer>{menu}</DropdownContainer>
                      )}
                    >
                      {renderTests(tests)}
                    </Select>
                  }
                />
              )}
              {stepType === "document_upload" && (
                <>
                  <div style={{ display: "none" }}>
                    {form.getFieldDecorator("config.documentType")(<Input />)}
                    {form.getFieldDecorator("config.uploadMode")(<Input />)}
                    {form.getFieldDecorator("config.templateFile")(<Input />)}
                    {form.getFieldDecorator("config.templateFileName")(
                      <Input />
                    )}
                  </div>
                  <FormItem
                    form={form}
                    label={t(
                      "opportunity:process.stepTypes.document_upload.requirementType"
                    )}
                    name="config.documentName"
                    initialValue={getDocumentUploadName(
                      t,
                      form.getFieldValue("config.documentType"),
                      form.getFieldValue("config.uploadMode")
                    )}
                    rules={[
                      {
                        required: true,
                        whitespace: true,
                        message: t("form:emptyFieldValidation")
                      }
                    ]}
                    render={
                      <Select
                        disabled={savingStep}
                        onChange={documentName => {
                          const option = R.find(
                            R.propEq("label", documentName),
                            documentUploadOptions
                          );

                          if (!option) return;

                          form.setFieldsValue({
                            "config.documentType": option.documentType,
                            "config.uploadMode": option.uploadMode,
                            ...(option.documentType !== "other"
                              ? {
                                  "config.customDocumentTypeName": undefined
                                }
                              : {}),
                            ...(option.documentType !== "form"
                              ? {
                                  "config.templateFile": undefined,
                                  "config.templateFileName": undefined
                                }
                              : {})
                          });
                        }}
                      >
                        {documentUploadOptions.map(option => (
                          <Option key={option.label} value={option.label}>
                            <Icon type={option.icon} /> {option.label}
                          </Option>
                        ))}
                      </Select>
                    }
                  />
                  {isOtherDocumentUpload && (
                    <FormItem
                      form={form}
                      label="Nombre personalizado"
                      name="config.customDocumentTypeName"
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: t("form:emptyFieldValidation")
                        }
                      ]}
                      render={<Input maxLength={160} disabled={savingStep} />}
                    />
                  )}
                  {isFormDocumentUpload && (
                    <TemplateRow>
                      <Upload
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx,.csv"
                        beforeUpload={file => {
                          uploadDocumentTemplate(file).catch(() => null);
                          return false;
                        }}
                        customRequest={({ onSuccess }) => {
                          if (onSuccess) onSuccess();
                        }}
                        showUploadList={false}
                      >
                        <TemplateUploadButton
                          icon="upload"
                          loading={uploadingTemplate}
                          disabled={savingStep || !step.idOpportunity}
                        >
                          {form.getFieldValue("config.templateFile")
                            ? t(
                                "opportunity:process.stepTypes.document_upload.replaceTemplate"
                              )
                            : t(
                                "opportunity:process.stepTypes.document_upload.uploadTemplate"
                              )}
                        </TemplateUploadButton>
                      </Upload>
                      <TemplateHelp>
                        {form.getFieldValue("config.templateFile")
                          ? form.getFieldValue("config.templateFileName") ||
                            t(
                              "opportunity:process.stepTypes.document_upload.templateUploaded"
                            )
                          : t(
                              "opportunity:process.stepTypes.document_upload.templateHelp"
                            )}
                      </TemplateHelp>
                    </TemplateRow>
                  )}
                </>
              )}
              {stepType !== "test" && inputKey && (
                <FormItem
                  form={form}
                  label={t(
                    `opportunity:process.stepTypes.${stepType}.${inputKey}`
                  )}
                  name={`config.${inputKey}`}
                  rules={[
                    // Only telephone input is not required
                    inputKey !== "tel" && {
                      required: true,
                      message: t("form:emptyFieldValidation")
                    }
                  ]}
                  render={
                    <Input
                      data-test="step-custom-input"
                      disabled={savingStep}
                    />
                  }
                />
              )}
              {stepType === "chatbot" && (
                <FormItem
                  form={form}
                  label={t(`opportunity:process.stepTypes.chatbot.title`)}
                  style={{ margin: 0 }}
                  name="config.chatTree.questions"
                  rules={[{ validator: chatbotValidators.getError }]}
                  help=""
                  validateStatus=""
                  initialValue={chatbotSelectors.getInitialValue(
                    t("opportunity:process.stepTypes.chatbot.initialMessage")
                  )}
                  render={
                    <OpportunityChatbot
                      idStep={step.id}
                      saveStep={onSubmit}
                      savingStep={savingStep}
                      warning={chatbotValidators.getWarning(
                        step,
                        candidatesList
                      )}
                      error={R.unless(
                        R.isNil,
                        R.head,
                        form.getFieldError("config.chatTree.questions")
                      )}
                      validateTree={callback =>
                        form.validateFields(
                          ["config.chatTree.questions"],
                          callback
                        )
                      }
                      // Prevent showing chatbot test in wizard
                      // (we can't start test conversation without the opportunity id)
                      disableChat={isWizard}
                    />
                  }
                />
              )}
            </>
          )}
          {stepType === "test" && step.name !== "selected" ? (
            <>
              <FormItem
                form={form}
                label={t(`opportunity:process.stepTypes.test.message`)}
                name="config.message"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: t("form:emptyFieldValidation")
                  }
                ]}
                initialValue={getChatbotMessage(
                  { order: step.order, type: stepType },
                  t
                )}
                data-test="test-default-message"
                render={
                  <TextArea
                    ref={messageInputRefs[0]}
                    disabled={savingStep}
                    autoSize={{ minRows: 4, maxRows: 8 }}
                  />
                }
                style={{ marginTop: 20, marginBottom: 10 }}
              />
              <Tags
                tags={getAvailableTags({ stepType, stepName: step.name, form })}
                textInputRef={messageInputRefs[0]}
                form={form}
                inputKey={inputKey}
                fieldName="message"
                t={t}
                insertTagInMessage={insertTagInMessage}
              />
              <FormItem
                form={form}
                label={t(
                  `opportunity:process.stepTypes.test.alternativeMessage`
                )}
                name="config.alternativeMessage"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: t("form:emptyFieldValidation")
                  }
                ]}
                initialValue={getChatbotMessage(
                  { order: step.order, type: stepType },
                  t,
                  "chatbotDoneTest"
                )}
                render={
                  <TextArea
                    ref={messageInputRefs[1]}
                    disabled={savingStep}
                    autoSize={{ minRows: 4, maxRows: 8 }}
                  />
                }
                style={{ marginTop: 20, marginBottom: 10 }}
              />
              <Tags
                tags={getAvailableTags({ stepType, stepName: step.name, form })}
                textInputRef={messageInputRefs[1]}
                form={form}
                inputKey={inputKey}
                fieldName="alternativeMessage"
                t={t}
                insertTagInMessage={insertTagInMessage}
              />
            </>
          ) : (
            stepTypeConfig.chatbotMessage !== false && (
              <>
                <FormItem
                  form={form}
                  label={t(`opportunity:process.stepDetails.chatbotMessage`)}
                  name="config.message"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: t("form:emptyFieldValidation")
                    }
                  ]}
                  render={
                    <TextArea
                      ref={messageInputRefs[0]}
                      disabled={savingStep}
                      autoSize={{ minRows: 4, maxRows: 8 }}
                    />
                  }
                />
                <Tags
                  tags={getAvailableTags({
                    stepType,
                    stepName: step.name,
                    form
                  })}
                  textInputRef={messageInputRefs[0]}
                  form={form}
                  inputKey={inputKey}
                  t={t}
                  insertTagInMessage={insertTagInMessage}
                />
              </>
            )
          )}

          {stepTypeConfig.schedule && step.name !== "selected" && (
            <Fragment>
              <FormItem
                form={form}
                name="config.autoSchedule"
                rules={[{ validator: autoScheduleValidator }]}
                decoratorProps={{ valuePropName: "checked" }}
                initialValue={false}
                render={
                  <Checkbox disabled={savingStep}>
                    {t(`opportunity:process.stepDetails.autoSchedule`)}
                  </Checkbox>
                }
              />
              {form.getFieldValue("config.autoSchedule") && (
                <Fragment>
                  <Row gutter={16}>
                    <Col span={6}>
                      <FormItem
                        form={form}
                        label={t(`opportunity:process.stepDetails.duration`)}
                        name="config.duration"
                        initialValue={30}
                        render={
                          <Select
                            className="duration-select-test"
                            disabled={savingStep}
                          >
                            {R.map(R.multiply(5), R.range(1, 13)).map(n => (
                              <Option value={n} key={n}>
                                {n} min
                              </Option>
                            ))}
                          </Select>
                        }
                      />
                    </Col>
                    <Col span={14} offset={1}>
                      <FormItem
                        form={form}
                        name="config.schedule"
                        validateStatus={
                          form.getFieldError("config.schedule") && "error"
                        }
                        help={
                          form.getFieldError("config.schedule") ||
                          t("opportunity:process.stepDetails.scheduleNote", {
                            timeZone: moment.tz
                              .guess()
                              .replace("/", " - ")
                              .replace("_", " ")
                          })
                        }
                        initialValue={{
                          from: moment()
                            .hour(9)
                            .minute(0)
                            .seconds(0),
                          to: moment()
                            .hour(17)
                            .minute(0)
                            .seconds(0)
                        }}
                        rules={[{ validator: scheduleValidator }]}
                        render={<SchedulePicker t={t} disabled={savingStep} />}
                      />
                    </Col>
                  </Row>
                  <FormItem
                    form={form}
                    label={t(`opportunity:process.stepDetails.calendar`)}
                    name="config.days"
                    initialValue={[1, 2, 3, 4, 5]}
                    rules={[
                      {
                        required: true,
                        message: t("form:emptyFieldValidation")
                      }
                    ]}
                    render={
                      <Checkbox.Group
                        options={weekDaysOptions}
                        disabled={savingStep}
                      />
                    }
                  />
                </Fragment>
              )}
            </Fragment>
          )}
          <Element
            display={stepTypeConfig.rooms ? "inherit" : "none"}
            className="rooms-container"
          >
            <FormItem
              form={form}
              label={t(`opportunity:process.stepDetails.rooms`)}
              name="rooms"
              initialValue={[]}
              rules={[{ validator: roomsValidator }]}
              render={<Rooms error={form.getFieldError("rooms")} />}
            />
          </Element>
          <Element display={["test"].includes(stepType) ? "inherit" : "none"}>
            <FormItem
              form={form}
              label={
                <span>
                  <Icon
                    type="thunderbolt"
                    style={{ color: "blue", marginRight: 3 }}
                  />
                  {t("opportunity:process.stepDetails.automaticStep")}
                </span>
              }
              name="automaticStep"
              style={{ marginTop: 20, marginBottom: 10 }}
            >
              <SwitchContainer>
                {form.getFieldDecorator("automaticStep", {
                  valuePropName: "checked",
                  initialValue: false
                })(
                  <Switch
                    error={form.getFieldError("automaticStep")}
                    disabled={savingStep}
                  />
                )}
                <SwitchInfo>
                  {t(`opportunity:process.stepDetails.automaticStepInfo`)}
                </SwitchInfo>
              </SwitchContainer>
            </FormItem>
          </Element>
          <Buttons>
            {!isWizard &&
            !step.unsaved && ( // En pasos ya existentes (con id), se guarda en la DB desde este mismo componente, en el onSubmit del form
                <Button
                  type="primary"
                  loading={savingStep}
                  htmlType="submit"
                  onClick={e => {
                    e.preventDefault();
                    onSubmit();
                  }}
                  data-test="save-step-details"
                >
                  {t(`opportunity:process.stepDetails.saveStep`)}
                </Button>
              )}
            {(isWizard || step.unsaved) &&
            onSave && ( // En pasos nuevos, el save se hace local
                <Tooltip
                  title={
                    !isWizard &&
                    t(`opportunity:process.stepDetails.saveAfterAddingStep`)
                  }
                >
                  <Button
                    type="primary"
                    data-test="save-step-details-wizard"
                    onClick={e => {
                      e.preventDefault();
                      form.validateFields(
                        async (err, values) => {
                          if (!err) {
                            if (values.type === "test") {
                              const [source, idTest] = R.split(
                                "-",
                                values.config.testId
                              );
                              values.config.source = source;
                              values.config.idTest = idTest;
                              delete values.config.testId;
                            }
                            if (values.type === "document_upload") {
                              values = applyDocumentRequirementConfig(
                                values,
                                documentRequirementOptions
                              );
                            }

                            // console.log("onSubmit2", values);
                            onSave(transformToApi(values));
                          }
                        },
                        { force: true }
                      );
                    }}
                  >
                    {t(`opportunity:process.stepDetails.saveStep`)}
                  </Button>
                </Tooltip>
              )}
          </Buttons>
        </Form>
      )}
    </Wrapper>
  );
};

const enhancer = compose(
  withTranslation(["opportunity", "form", "global"]),
  connect(
    (state, ownProps) => {
      const { allMatches, step, isWizard, t } = ownProps;

      return {
        candidatesList: allMatches,
        opportunityId: state.opportunity.selected.id,
        step: R.pipe(
          R.defaultTo({}),
          R.when(
            R.always(!isWizard && !step.unsaved), // If step is saved, show rooms from selectedStep.rooms
            R.assoc(
              "rooms",
              R.pathOr(
                [],
                ["opportunity", "selected", "selectedStep", "rooms"],
                state
              )
            )
          ),
          R.evolve({
            rooms: R.map(
              R.evolve({
                candidates: R.map(candidate =>
                  R.propOr({ id: candidate }, candidate, allMatches)
                )
              })
            )
          })
        )(step),
        companyUsers: R.defaultTo([], state.company.users),
        documentRequirementOptions: getDocumentRequirementOptions(t),
        tests: FETCH_TESTS.getResponse(state) || []
      };
    },
    {
      uploadOpportunityDocument,
      updateOpportunityStep
    }
  ),
  Form.create(),
  dispatchOnMount(({ opportunityId, step, isWizard }) =>
    !isWizard && !step.unsaved
      ? fetchOpportunityRooms({ opportunityId, step })
      : () => {}
  ),
  isLoading(FETCH_OPPORTUNITY_ROOMS, { prop: "stepLoading" }),
  isLoading(UPDATE_OPPORTUNITY_STEP, { prop: "savingStep" }),
  lifecycle({
    componentDidUpdate(prevProps) {
      const { form, step, stepLoading, onChange, t } = this.props;

      if (typeof onChange === "function") {
        onChange(form.getFieldsValue());
      }

      if (prevProps.stepLoading && !stepLoading) {
        if (step.type === "test-gh") {
          step.type = "test";
          step.config.source = "gh";
        }

        const formData = transformFromApi(step);

        if (formData.type === "test") {
          formData.config.testId = `${formData.config.source}-${formData.config.idTest}`;
        }
        if (formData.type === "document_upload") {
          formData.config = updateDocumentRequirementName(formData.config, t);
          formData.config.documentRequirementKey = getDocumentRequirementKey(
            formData.config
          );
        }
        // console.log("componentDidUpdate", formData);

        // Register custom input to prevent error "You cannot set field before registering it"
        const inputKey = getProcessStepTypeConfig(step.type).inputKey;
        if (inputKey) {
          form.getFieldDecorator(`config.${inputKey}`, {
            initialValue: ""
          });
        }
        form.getFieldDecorator("config.testId");
        form.getFieldDecorator("config.documentRequirementKey");
        form.getFieldDecorator("config.source");
        form.getFieldDecorator("config.duration");
        form.getFieldDecorator("config.schedule");
        form.getFieldDecorator("config.days");
        form.getFieldDecorator("config.chatTree.questions");
        form.getFieldDecorator("config.alternativeMessage");
        form.getFieldDecorator("config.documentType");
        form.getFieldDecorator("config.uploadMode");
        form.getFieldDecorator("config.documentName");
        form.getFieldDecorator("config.customDocumentTypeName");
        form.getFieldDecorator("config.templateFile");
        form.getFieldDecorator("config.templateFileName");
        form.getFieldDecorator("rooms");
        form.setFieldsValue(R.pick(R.keys(form.getFieldsValue()), formData));
      }
    },
    componentDidMount() {
      const { form, step, isWizard, t } = this.props;

      if (isWizard || step.unsaved) {
        if (step.type === "test-gh") {
          step.type = "test";
          step.config.source = "gh";
        }

        const formData = transformFromApi(step);

        if (formData.type === "test") {
          formData.config.testId = `${formData.config.source}-${formData.config.idTest}`;
        }
        if (formData.type === "document_upload") {
          formData.config = updateDocumentRequirementName(formData.config, t);
          formData.config.documentRequirementKey = getDocumentRequirementKey(
            formData.config
          );
        }
        // console.log("componentDidMount", formData);

        // Register custom input to prevent error "You cannot set field before registering it"
        const inputKey = getProcessStepTypeConfig(step.type).inputKey;
        if (inputKey) {
          form.getFieldDecorator(`config.${inputKey}`, {
            initialValue: ""
          });
        }
        form.getFieldDecorator("config.testId");
        form.getFieldDecorator("config.documentRequirementKey");
        form.getFieldDecorator("config.source");
        form.getFieldDecorator("config.duration");
        form.getFieldDecorator("config.schedule");
        form.getFieldDecorator("config.days");
        form.getFieldDecorator("config.chatTree.questions");
        form.getFieldDecorator("config.alternativeMessage");
        form.getFieldDecorator("config.documentType");
        form.getFieldDecorator("config.uploadMode");
        form.getFieldDecorator("config.documentName");
        form.getFieldDecorator("config.customDocumentTypeName");
        form.getFieldDecorator("config.templateFile");
        form.getFieldDecorator("config.templateFileName");
        form.getFieldDecorator("rooms");
        form.setFieldsValue(R.pick(R.keys(form.getFieldsValue()), formData));
      }
    }
  }),
  withProps(({ t, form, companyUsers }) => {
    const weekDaysOptions = [
      [1, "monday"],
      [2, "tuesday"],
      [3, "wednesday"],
      [4, "thursday"],
      [5, "friday"],
      [6, "saturday"],
      [0, "sunday"]
    ].map(([dayValue, dayName]) => ({
      value: dayValue,
      label: t(`opportunity:process.stepDetails.weekDays.${dayName}`)
    }));

    const stepType = form.getFieldValue("type") || defaultStepType;
    const stepTypeConfig = getProcessStepTypeConfig(stepType);

    const scheduleValidator = (rule, value, callback) => {
      if (!value.from || !value.to) {
        callback(t("form:emptyFieldValidation"));
      } else if (value.from >= value.to) {
        callback(t("opportunity:process.stepDetails.invalidToTime"));
      } else {
        callback();
      }
    };

    const autoScheduleValidator = (rule, value, callback) => {
      const user = form.getFieldValue("config.users");
      if (!value) return callback();

      if (!user) {
        return callback(
          t("opportunity:process.stepDetails.interviewerRequired")
        );
      }

      if (
        !R.propOr(
          false,
          "hasCalendar",
          R.find(R.propEq("id", user.key), companyUsers)
        )
      ) {
        return callback(
          t("opportunity:process.stepDetails.interviewerNoCalendar")
        );
      }

      callback();
    };

    const roomsValidator = (rule, value, callback) => {
      const user = form.getFieldValue("config.users");

      if (stepTypeConfig.rooms) {
        if (!value) return callback();

        if (!user) {
          return callback(
            t("opportunity:process.stepDetails.interviewerRequired")
          );
        }

        if (
          !R.propOr(
            false,
            "hasCalendar",
            R.find(R.propEq("id", user.key), companyUsers)
          )
        ) {
          return callback(
            t("opportunity:process.stepDetails.interviewerNoCalendar")
          );
        }
      }

      callback();
    };

    return {
      weekDaysOptions,
      scheduleValidator,
      autoScheduleValidator,
      roomsValidator,
      stepType,
      stepTypeConfig,
      inputKey: stepTypeConfig.inputKey
    };
  }),
  withHandlers({
    handleTypeChange: ({ step, t, form }) => newStepType => {
      form.setFieldsValue({
        "config.message": getChatbotMessage(
          { order: step.order, type: newStepType },
          t
        )
      });
    },
    insertTagInMessage: ({ form }) => (
      inputRef,
      tag,
      fieldName = "message"
    ) => {
      if (!inputRef || !inputRef.current) {
        return;
      }

      const field = `config.${fieldName}`;
      const message = form.getFieldValue(field);
      const textArea = inputRef.current.resizableTextArea.textArea;
      const { selectionStart, selectionEnd } = textArea;

      form.setFieldsValue(
        {
          [field]:
            message.slice(0, selectionStart) + tag + message.slice(selectionEnd)
        },
        () => {
          const newCursorPosition = selectionStart + tag.length;
          textArea.focus();
          textArea.setSelectionRange(newCursorPosition, newCursorPosition);
        }
      );
    },
    onSubmit: ({
      form,
      documentRequirementOptions,
      opportunityId,
      step,
      updateOpportunityStep,
      onSave,
      updateStep
    }) => ({ onResolved, resetEditingStep } = {}) => {
      form.validateFields(
        async (err, values) => {
          if (!err) {
            if (values.type === "test") {
              const [source, idTest] = R.split("-", values.config.testId);
              values.config.source = source;
              values.config.idTest = idTest;
              delete values.config.testId;
            }
            if (values.type === "document_upload") {
              values = applyDocumentRequirementConfig(
                values,
                documentRequirementOptions
              );
            }

            // console.log("onSubmit", values);

            const newStep = await updateOpportunityStep({
              opportunityId,
              stepId: step.id,
              formData: transformToApi(values)
            });

            onResolved && onResolved();

            updateStep(
              step.id,
              R.evolve({
                rooms: R.reject(R.prop("isDeleted"))
              })(transformToApi(values))
            );

            if (onSave && typeof onSave === "function") {
              onSave(newStep, resetEditingStep);
            }
          }
        },
        { force: true }
      );
    }
  })
);

export default enhancer(StepConfiguration);
